import React, { useEffect, useState } from "react";
import { Container, Box, Typography, IconButton } from "@material-ui/core";
import styled from "styled-components";
import { Pagination } from "@material-ui/lab";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { COLORS } from "../../assets/colors";
import api from "../../services/api";
import CircularLoader from "../../components/UI/Loader";
import NoData from "../../components/NoData";
import { RefreshRounded } from "@material-ui/icons";

export default function RideWishesPublic() {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const pageSize = 10;
  const limit = 10;
  const offset = (page - 1) * pageSize;
  const pages = count > 0 ? Math.ceil(count / pageSize) : 0;

  useEffect(() => {
    fetchWishes();
  }, [page]);

  const fetchWishes = async () => {
    try {
      setLoading(true);
      const response = await api.get("/ride-wishes/public", {
        params: { limit, offset },
      });
      if (response.status === 200) {
        setWishes(response.data.data);
        setCount(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching ride wishes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, value) => {
    setPage(value);
  };

  const handleOfferRide = (wish) => {
    navigate(
      `/publish?leavingFrom=${encodeURIComponent(wish.leaving_from)}&goingTo=${encodeURIComponent(wish.heading_to)}&wishId=${wish.id}`
    );
  };

  return (
    <div>
      <Helmet>
        <title>Rides People Need - GoSawari</title>
        <meta
          name="description"
          content="See what rides passengers are looking for on GoSawari. Offer a ride and help fellow travelers."
        />
        <link rel="canonical" href="https://gosawari.com/ride-wishes" />
      </Helmet>

      <HeroBanner>
        <Container maxWidth="md">
          <h1>🔔 Rides People Need</h1>
          <p>
            Passengers couldn't find a ride and submitted these requests.
            If you're headed that way, offer a ride and earn!
          </p>
        </Container>
      </HeroBanner>

      <Container maxWidth="md">
        <ContentSection>
          <SectionHeader>
            <div>
              <h2>Active Requests</h2>
              <span className="count">{count} requests</span>
            </div>
            <IconButton onClick={fetchWishes} size="small">
              <RefreshRounded />
            </IconButton>
          </SectionHeader>

          {loading ? (
            <CircularLoader isLoading={loading} />
          ) : wishes.length === 0 ? (
            <NoData
              showImage={true}
              message="No ride requests at the moment. Check back later!"
              button={false}
            />
          ) : (
            <WishGrid>
              {wishes.map((wish) => (
                <WishCard key={wish.id}>
                  <div className="card-header">
                    <div className="route">
                      <span className="from">{wish.leaving_from}</span>
                      <span className="arrow">→</span>
                      <span className="to">{wish.heading_to}</span>
                    </div>
                    <div className="seats-badge">{wish.seats} seat{wish.seats > 1 ? "s" : ""}</div>
                  </div>

                  <div className="card-details">
                    <div className="detail">
                      <span className="label">📅 Date:</span>
                      <span className="value">
                        {wish.preferred_date
                          ? moment(wish.preferred_date).format("MMM Do, YYYY")
                          : "Flexible"}
                      </span>
                    </div>
                    {wish.note && (
                      <div className="detail">
                        <span className="label">💬 Note:</span>
                        <span className="value note">{wish.note}</span>
                      </div>
                    )}
                    <div className="detail">
                      <span className="label">🕐 Requested:</span>
                      <span className="value">
                        {moment(wish.createdAt).fromNow()}
                      </span>
                    </div>
                  </div>

                  <div className="card-footer">
                    {wish.requestedBy && (
                      <div className="requester">
                        <img
                          src={
                            wish.requestedBy.avatar_url ||
                            `https://ui-avatars.com/api/?name=${wish.requestedBy.first_name}&background=054752&color=fff`
                          }
                          alt=""
                        />
                        <span>{wish.requestedBy.first_name}</span>
                      </div>
                    )}
                    <button
                      className="offer-btn"
                      onClick={() => handleOfferRide(wish)}
                    >
                      🚗 Offer This Ride
                    </button>
                  </div>
                </WishCard>
              ))}
            </WishGrid>
          )}

          {pages > 1 && (
            <PaginationWrapper>
              <Pagination
                onChange={handleChange}
                size="large"
                page={page}
                count={pages}
                color="primary"
              />
            </PaginationWrapper>
          )}
        </ContentSection>
      </Container>
    </div>
  );
}

const HeroBanner = styled.div`
  background: ${COLORS.backgroundGradient};
  padding: 2.5rem 0;
  text-align: center;
  color: white;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 1.05rem;
    opacity: 0.9;
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ContentSection = styled.div`
  padding: 2rem 0 3rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${COLORS.primaryColor};
    margin: 0;
  }

  .count {
    font-size: 0.9rem;
    color: ${COLORS.secondaryTextColor};
    margin-top: 0.25rem;
    display: block;
  }
`;

const WishGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const WishCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid ${COLORS.borderColor};
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .card-header {
    padding: 1.25rem 1.25rem 0.75rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .route {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;

      .from,
      .to {
        font-weight: 700;
        font-size: 1.05rem;
        color: ${COLORS.primaryColor};
      }

      .arrow {
        color: ${COLORS.secondaryTextColor};
        font-size: 1.1rem;
      }
    }

    .seats-badge {
      background: rgba(5, 71, 82, 0.1);
      color: ${COLORS.primaryColor};
      padding: 0.3rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.8rem;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  .card-details {
    padding: 0 1.25rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;

    .detail {
      display: flex;
      gap: 0.5rem;
      font-size: 0.88rem;

      .label {
        color: ${COLORS.secondaryTextColor};
        white-space: nowrap;
      }

      .value {
        color: ${COLORS.secondaryColor};
        font-weight: 500;
      }

      .note {
        font-style: italic;
        font-weight: 400;
      }
    }
  }

  .card-footer {
    padding: 0.75rem 1.25rem;
    border-top: 1px solid ${COLORS.borderColor};
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fafbfc;

    .requester {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      img {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        object-fit: cover;
      }

      span {
        font-size: 0.85rem;
        color: ${COLORS.secondaryTextColor};
        font-weight: 500;
      }
    }

    .offer-btn {
      background: ${COLORS.primaryColor};
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 8px;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        background: ${COLORS.primaryLight500};
        box-shadow: 0 4px 12px rgba(5, 71, 82, 0.3);
      }
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 0;
`;
